import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Key,
  Lock,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Building2,
  UserCircle2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useEntitlements } from "@/modules/useEntitlements";
import { WORK_MODULES } from "@/modules/workModuleCore";

const TRAFFIC = {
  green: "border-emerald-300 bg-emerald-50 text-emerald-950",
  yellow: "border-amber-300 bg-amber-50 text-amber-950",
  red: "border-rose-300 bg-rose-50 text-rose-950",
  gray: "border-slate-200 bg-slate-50 text-slate-700",
};

function TrafficBadge({ light, days }) {
  const cls = TRAFFIC[light] || TRAFFIC.gray;
  const label =
    light === "green"
      ? `Vigente${days != null ? ` · ${days} d` : ""}`
      : light === "yellow"
        ? `Por vencer${days != null ? ` · ${days} d` : ""}`
        : light === "red"
          ? days != null && days < 0
            ? `Vencida · ${-days} d`
            : "Urgente"
          : "Sin configurar";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm border ${cls}`}>
      {light === "green" ? <CheckCircle2 className="w-3 h-3" /> : light === "gray" ? null : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

/**
 * Gestión centralizada de credenciales del cliente (FIEL, rutas CSD/IMSS, registros patronales).
 */
export default function ClientCredentialsPanel({ clientId, clientMeta }) {
  const { isWorkModuleEntitled, loading: entitlementsLoading } = useEntitlements();
  const canAccessDescargaSat = isWorkModuleEntitled(WORK_MODULES.VISOR_SAT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fielBusy, setFielBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [paths, setPaths] = useState({
    fiel_socio_path: "",
    csd_path: "",
    imss_certificado_path: "",
    registros_patronales: "",
  });
  const [cerFile, setCerFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [fielPass, setFielPass] = useState("");
  const [imssBusy, setImssBusy] = useState(false);
  const [repFielBusy, setRepFielBusy] = useState(false);
  const [pfxFile, setPfxFile] = useState(null);
  const [pfxPass, setPfxPass] = useState("");
  const [imssForm, setImssForm] = useState({
    usuario_idse: "",
    contrasena_idse: "",
    registro_patronal_matriz: "",
  });
  const [repCerFile, setRepCerFile] = useState(null);
  const [repKeyFile, setRepKeyFile] = useState(null);
  const [repFielPass, setRepFielPass] = useState("");
  const [fielReplaceMode, setFielReplaceMode] = useState(false);
  const cerRef = useRef(null);
  const keyRef = useRef(null);
  const pfxRef = useRef(null);
  const repCerRef = useRef(null);
  const repKeyRef = useRef(null);

  const load = async () => {
    if (!clientId) {
      setCreds(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/credentials`);
      setCreds(data);
      setPaths({
        fiel_socio_path: data.fiel_socio_path || "",
        csd_path: data.csd_path || "",
        imss_certificado_path: data.imss_certificado_path || "",
        registros_patronales: (data.registros_patronales || []).join("\n"),
      });
      const imss = data.imss_matriz_status || {};
      setImssForm((prev) => ({
        ...prev,
        usuario_idse: imss.usuario_idse || prev.usuario_idse,
        registro_patronal_matriz: imss.registro_patronal_matriz || prev.registro_patronal_matriz,
      }));
    } catch (e) {
      setCreds(null);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar credenciales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFielReplaceMode(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const onSaveFiel = async () => {
    if (!clientId || !cerFile || !keyFile || !fielPass.trim()) {
      toast.error("Seleccione .cer, .key y contraseña");
      return;
    }
    setFielBusy(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("password", fielPass);
    fd.append("cer_file", cerFile);
    fd.append("key_file", keyFile);
    try {
      const { data: fielRes } = await api.post("/sat/credenciales-fiel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const syncMsg = fielRes?.sat_sync_activation?.ui_message;
      if (syncMsg) {
        toast.success(syncMsg);
      } else if (fielReplaceMode) {
        toast.success("e.firma sustituida correctamente");
      } else {
        toast.success("e.firma guardada de forma segura");
      }
      setFielPass("");
      setCerFile(null);
      setKeyFile(null);
      setFielReplaceMode(false);
      if (cerRef.current) cerRef.current.value = "";
      if (keyRef.current) keyRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la e.firma");
    } finally {
      setFielBusy(false);
    }
  };

  const onSaveImssMatriz = async () => {
    if (!clientId || !pfxFile || !pfxPass.trim()) {
      toast.error("Seleccione .pfx y contraseña del certificado IMSS");
      return;
    }
    setImssBusy(true);
    const fd = new FormData();
    fd.append("pfx_file", pfxFile);
    fd.append("pfx_password", pfxPass);
    fd.append("usuario_idse", imssForm.usuario_idse.trim());
    fd.append("contrasena_idse", imssForm.contrasena_idse);
    fd.append("registro_patronal_matriz", imssForm.registro_patronal_matriz.trim());
    try {
      await api.post(`/clients/${clientId}/credentials/imss-matriz`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Certificado IMSS matriz guardado de forma segura");
      setPfxPass("");
      setImssForm((p) => ({ ...p, contrasena_idse: "" }));
      setPfxFile(null);
      if (pfxRef.current) pfxRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar el certificado IMSS");
    } finally {
      setImssBusy(false);
    }
  };

  const onSaveRepFiel = async () => {
    if (!clientId || !repCerFile || !repKeyFile || !repFielPass.trim()) {
      toast.error("Seleccione .cer, .key y contraseña del representante");
      return;
    }
    setRepFielBusy(true);
    const fd = new FormData();
    fd.append("password", repFielPass);
    fd.append("cer_file", repCerFile);
    fd.append("key_file", repKeyFile);
    try {
      await api.post(`/clients/${clientId}/credentials/fiel-representante`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("e.firma del representante guardada de forma segura");
      setRepFielPass("");
      setRepCerFile(null);
      setRepKeyFile(null);
      if (repCerRef.current) repCerRef.current.value = "";
      if (repKeyRef.current) repKeyRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la e.firma del representante");
    } finally {
      setRepFielBusy(false);
    }
  };

  const onSavePaths = async () => {
    if (!clientId) return;
    setSaving(true);
    try {
      const registros = paths.registros_patronales
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      await api.put(`/clients/${clientId}/credentials`, {
        fiel_socio_path: paths.fiel_socio_path.trim() || null,
        csd_path: paths.csd_path.trim() || null,
        imss_certificado_path: paths.imss_certificado_path.trim() || null,
        registros_patronales: registros,
      });
      toast.success("Rutas y registros actualizados");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!clientId) {
    return (
      <Alert className="border-slate-200">
        <AlertTitle className="text-sm">Seleccione un cliente de trabajo</AlertTitle>
        <AlertDescription className="text-xs">
          Use el botón <strong>Usar</strong> en la tabla o el selector del Panel General para gestionar credenciales.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando credenciales…
      </div>
    );
  }

  const fielStatus = creds?.fiel_status || {};
  const traffic = creds?.fiel_status?.days_remaining != null
    ? (fielStatus.days_remaining < 0 || fielStatus.days_remaining <= 15
        ? "red"
        : fielStatus.days_remaining <= 30
          ? "yellow"
          : "green")
    : creds?.fiel_rfc_certificado
      ? "green"
      : "gray";

  const imssStatus = creds?.imss_matriz_status || {};
  const imssTraffic = imssStatus.configured ? "green" : "gray";
  const repStatus = creds?.fiel_representante_status || {};
  const repTraffic = repStatus.days_remaining != null
    ? (repStatus.days_remaining < 0 || repStatus.days_remaining <= 15
        ? "red"
        : repStatus.days_remaining <= 30
          ? "yellow"
          : "green")
    : repStatus.configured
      ? "green"
      : "gray";

  const fielConfigured = Boolean(creds?.fiel_rfc_certificado || fielStatus.configured);
  const showFielUploadForm = !fielConfigured || fielReplaceMode;

  const startFielReplace = () => {
    setFielReplaceMode(true);
    setCerFile(null);
    setKeyFile(null);
    setFielPass("");
    if (cerRef.current) cerRef.current.value = "";
    if (keyRef.current) keyRef.current.value = "";
  };

  const cancelFielReplace = () => {
    setFielReplaceMode(false);
    setCerFile(null);
    setKeyFile(null);
    setFielPass("");
    if (cerRef.current) cerRef.current.value = "";
    if (keyRef.current) keyRef.current.value = "";
  };

  return (
    <div className="space-y-4" data-testid="client-credentials-panel">
      <p className="text-[11px] text-muted-foreground m-0">
        Abra la sección que necesite. Por defecto todas quedan retraídas.
      </p>

      <Accordion
        type="multiple"
        defaultValue={[]}
        className="rounded-md border border-slate-200 bg-white px-3"
        data-testid="client-credentials-accordion"
      >
        <AccordionItem value="fiel-sat" className="border-b-0">
          <AccordionTrigger className="text-xs font-medium py-3 hover:no-underline">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2 text-left">
              <span className="font-bold text-[#0F2D5C]">e.firma (FIEL) — SAT</span>
              <TrafficBadge light={traffic} days={fielStatus.days_remaining} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-xs text-slate-600 m-0">
                Fuente única para descargas automáticas. Cliente:{" "}
                <strong>{clientMeta?.razon_social || creds?.client_name || "—"}</strong>{" "}
                <span className="font-mono">({clientMeta?.rfc || creds?.client_rfc})</span>
              </p>
              {fielConfigured && !fielReplaceMode && !showFielUploadForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[10px] font-semibold border-[#0F2D5C] text-[#0F2D5C]"
                  onClick={startFielReplace}
                  data-testid="fiel-replace-start-header"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Sustituir FIEL actual
                </Button>
              ) : null}
            </div>

            {creds?.fiel_rfc_certificado ? (
              <div className="text-[11px] rounded border border-emerald-200 bg-emerald-50/80 p-3 text-emerald-950">
                RFC certificado: <span className="font-mono">{creds.fiel_rfc_certificado}</span>
                {creds.fiel_expiry ? (
                  <span className="ml-2">· Vigencia: {String(creds.fiel_expiry).slice(0, 10)}</span>
                ) : null}
              </div>
            ) : null}

            {fielConfigured && !showFielUploadForm ? (
              <p className="text-[10px] text-slate-600 m-0">
                La e.firma vigente se usa en descargas SAT, CSF y opinión de cumplimiento. Use{" "}
                <strong>Sustituir FIEL actual</strong> si renovó el certificado o debe volver a cargar los archivos.
              </p>
            ) : null}

            {showFielUploadForm ? (
              <>
                {fielReplaceMode ? (
                  <Alert className="border-amber-200 bg-amber-50/90 py-2">
                    <AlertTitle className="text-xs font-bold text-amber-950">Sustituir e.firma</AlertTitle>
                    <AlertDescription className="text-[10px] text-amber-900">
                      Seleccione el nuevo par <strong>.cer</strong> / <strong>.key</strong> y la contraseña. Al confirmar,
                      reemplazará la FIEL guardada (mismo RFC del cliente). Las descargas SAT usarán la nueva e.firma.
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="rounded-md border border-emerald-200 bg-emerald-50/90 p-3 text-[10px] text-emerald-950 leading-relaxed">
                  <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
                  Los archivos <code className="font-mono">.key</code> y la contraseña se almacenan cifrados en MongoDB.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fiel-upload-form">
                  <div>
                    <Label className="text-[10px] flex items-center gap-1"><Key className="w-3 h-3" /> Archivo .CER</Label>
                    <input ref={cerRef} type="file" accept=".cer" className="hidden" onChange={(e) => setCerFile(e.target.files?.[0] ?? null)} />
                    <Button type="button" variant="outline" size="sm" className="h-8 w-full text-[10px] mt-1" onClick={() => cerRef.current?.click()}>
                      Seleccionar .cer
                    </Button>
                    <p className="text-[9px] text-slate-500 truncate mt-0.5">{cerFile?.name || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] flex items-center gap-1"><Lock className="w-3 h-3" /> Archivo .KEY</Label>
                    <input ref={keyRef} type="file" accept=".key" className="hidden" onChange={(e) => setKeyFile(e.target.files?.[0] ?? null)} />
                    <Button type="button" variant="outline" size="sm" className="h-8 w-full text-[10px] mt-1" onClick={() => keyRef.current?.click()}>
                      Seleccionar .key
                    </Button>
                    <p className="text-[9px] text-slate-500 truncate mt-0.5">{keyFile?.name || "—"}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Contraseña de la llave privada</Label>
                  <Input type="password" value={fielPass} onChange={(e) => setFielPass(e.target.value)} className="h-8 text-xs mt-1" autoComplete="new-password" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={onSaveFiel}
                    disabled={fielBusy}
                    className="bg-[#0F2D5C] hover:bg-[#0a2247] h-9 text-xs font-bold"
                    data-testid="fiel-save-button"
                  >
                    {fielBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    {fielReplaceMode ? "Confirmar sustitución de e.firma" : "Guardar e.firma de forma segura"}
                  </Button>
                  {fielReplaceMode ? (
                    <Button type="button" variant="ghost" size="sm" className="h-9 text-xs" onClick={cancelFielReplace} disabled={fielBusy}>
                      Cancelar
                    </Button>
                  ) : null}
                </div>
              </>
            ) : null}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="imss-matriz" className="border-b-0 border-t" data-testid="imss-matriz-card">
          <AccordionTrigger className="text-xs font-medium py-3 hover:no-underline">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2 text-left">
              <span className="font-bold text-[#0F2D5C] inline-flex items-center gap-1.5">
                <Building2 className="w-4 h-4 shrink-0" /> Certificado IMSS — Matriz
              </span>
              <TrafficBadge light={imssTraffic} days={null} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <p className="text-xs text-slate-600 m-0">
              Certificado <strong>.pfx</strong> y acceso IDSE del registro patronal matriz. Las obras pueden reutilizarlo en{" "}
              <strong>Registros patronales</strong>.
            </p>

            {imssStatus.configured ? (
              <div className="text-[11px] rounded border border-emerald-200 bg-emerald-50/80 p-3 text-emerald-950">
                Archivo: <span className="font-mono">{imssStatus.pfx_nombre || "certificado.pfx"}</span>
                {imssStatus.usuario_idse ? (
                  <span className="ml-2">· Usuario IDSE: <span className="font-mono">{imssStatus.usuario_idse}</span></span>
                ) : null}
                {imssStatus.registro_patronal_matriz ? (
                  <span className="ml-2">· RP matriz: <span className="font-mono">{imssStatus.registro_patronal_matriz}</span></span>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-md border border-blue-200 bg-blue-50/90 p-3 text-[10px] text-blue-950 leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
              El <code className="font-mono">.pfx</code>, contraseñas IDSE y del certificado se almacenan cifrados en MongoDB.
            </div>

            <div>
              <Label className="text-[10px]">Archivo .PFX (certificado IMSS)</Label>
              <input ref={pfxRef} type="file" accept=".pfx" className="hidden" onChange={(e) => setPfxFile(e.target.files?.[0] ?? null)} />
              <Button type="button" variant="outline" size="sm" className="h-8 w-full text-[10px] mt-1 max-w-xs" onClick={() => pfxRef.current?.click()}>
                Seleccionar .pfx
              </Button>
              <p className="text-[9px] text-slate-500 truncate mt-0.5">{pfxFile?.name || imssStatus.pfx_nombre || "—"}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px]">Contraseña del .pfx</Label>
                <Input type="password" value={pfxPass} onChange={(e) => setPfxPass(e.target.value)} className="h-8 text-xs mt-1" autoComplete="new-password" />
              </div>
              <div>
                <Label className="text-[10px]">Registro patronal matriz (opcional)</Label>
                <Input
                  value={imssForm.registro_patronal_matriz}
                  onChange={(e) => setImssForm((p) => ({ ...p, registro_patronal_matriz: e.target.value.toUpperCase() }))}
                  className="h-8 text-xs font-mono mt-1"
                  placeholder="E0012345678"
                />
              </div>
              <div>
                <Label className="text-[10px]">Usuario IDSE</Label>
                <Input value={imssForm.usuario_idse} onChange={(e) => setImssForm((p) => ({ ...p, usuario_idse: e.target.value }))} className="h-8 text-xs mt-1" />
              </div>
              <div>
                <Label className="text-[10px]">Contraseña IDSE</Label>
                <Input type="password" value={imssForm.contrasena_idse} onChange={(e) => setImssForm((p) => ({ ...p, contrasena_idse: e.target.value }))} className="h-8 text-xs mt-1" autoComplete="new-password" />
              </div>
            </div>
            <Button type="button" onClick={onSaveImssMatriz} disabled={imssBusy} className="bg-[#0F2D5C] hover:bg-[#0a2247] h-9 text-xs font-bold">
              {imssBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Guardar certificado IMSS matriz
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="fiel-representante" className="border-b-0 border-t" data-testid="fiel-representante-card">
          <AccordionTrigger className="text-xs font-medium py-3 hover:no-underline">
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2 text-left">
              <span className="font-bold text-[#0F2D5C] inline-flex items-center gap-1.5">
                <UserCircle2 className="w-4 h-4 shrink-0" /> e.firma representante legal (opcional)
              </span>
              <TrafficBadge light={repTraffic} days={repStatus.days_remaining} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <p className="text-xs text-slate-600 m-0">
              Segunda e.firma para trámites firmados por el representante. No sustituye la FIEL SAT del RFC del cliente.
            </p>

            {repStatus.configured && repStatus.rfc_certificado ? (
              <div className="text-[11px] rounded border border-emerald-200 bg-emerald-50/80 p-3 text-emerald-950">
                RFC certificado: <span className="font-mono">{repStatus.rfc_certificado}</span>
                {repStatus.fiel_expiry ? (
                  <span className="ml-2">· Vigencia: {String(repStatus.fiel_expiry).slice(0, 10)}</span>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-md border border-emerald-200 bg-emerald-50/90 p-3 text-[10px] text-emerald-950 leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
              Los archivos <code className="font-mono">.key</code> y la contraseña se almacenan cifrados en MongoDB.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] flex items-center gap-1"><Key className="w-3 h-3" /> Archivo .CER</Label>
                <input ref={repCerRef} type="file" accept=".cer" className="hidden" onChange={(e) => setRepCerFile(e.target.files?.[0] ?? null)} />
                <Button type="button" variant="outline" size="sm" className="h-8 w-full text-[10px] mt-1" onClick={() => repCerRef.current?.click()}>
                  Seleccionar .cer
                </Button>
                <p className="text-[9px] text-slate-500 truncate mt-0.5">{repCerFile?.name || "—"}</p>
              </div>
              <div>
                <Label className="text-[10px] flex items-center gap-1"><Lock className="w-3 h-3" /> Archivo .KEY</Label>
                <input ref={repKeyRef} type="file" accept=".key" className="hidden" onChange={(e) => setRepKeyFile(e.target.files?.[0] ?? null)} />
                <Button type="button" variant="outline" size="sm" className="h-8 w-full text-[10px] mt-1" onClick={() => repKeyRef.current?.click()}>
                  Seleccionar .key
                </Button>
                <p className="text-[9px] text-slate-500 truncate mt-0.5">{repKeyFile?.name || "—"}</p>
              </div>
            </div>
            <div>
              <Label className="text-[10px]">Contraseña de la llave privada</Label>
              <Input type="password" value={repFielPass} onChange={(e) => setRepFielPass(e.target.value)} className="h-8 text-xs mt-1" autoComplete="new-password" />
            </div>
            <Button type="button" onClick={onSaveRepFiel} disabled={repFielBusy} variant="outline" className="h-9 text-xs font-bold border-[#0F2D5C] text-[#0F2D5C]">
              {repFielBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Guardar e.firma del representante
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="rutas-complementarias" className="border-b-0 border-t">
          <AccordionTrigger className="text-xs font-medium py-3 hover:no-underline">
            <span className="font-bold text-[#0F2D5C] text-left">Rutas complementarias</span>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-[10px]">FIEL socio / representante (ruta carpeta)</Label>
                <Input value={paths.fiel_socio_path} onChange={(e) => setPaths((p) => ({ ...p, fiel_socio_path: e.target.value }))} className="h-8 text-xs font-mono mt-1" />
              </div>
              <div>
                <Label className="text-[10px]">CSD (ruta carpeta)</Label>
                <Input value={paths.csd_path} onChange={(e) => setPaths((p) => ({ ...p, csd_path: e.target.value }))} className="h-8 text-xs font-mono mt-1" />
              </div>
              <div>
                <Label className="text-[10px]">Certificado IMSS (ruta)</Label>
                <Input value={paths.imss_certificado_path} onChange={(e) => setPaths((p) => ({ ...p, imss_certificado_path: e.target.value }))} className="h-8 text-xs font-mono mt-1" />
              </div>
              <div>
                <Label className="text-[10px]">Registros patronales (uno por línea)</Label>
                <textarea
                  value={paths.registros_patronales}
                  onChange={(e) => setPaths((p) => ({ ...p, registros_patronales: e.target.value }))}
                  rows={4}
                  className="w-full mt-1 rounded-sm border border-slate-200 px-2 py-1.5 text-xs font-mono"
                  spellCheck={false}
                  placeholder="E0012345678&#10;..."
                />
              </div>
            </div>
            <Button type="button" variant="outline" onClick={onSavePaths} disabled={saving} className="h-9 text-xs">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guardar rutas y registros
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {canAccessDescargaSat && !entitlementsLoading ? (
        <p className="text-[10px] text-slate-500 m-0">
          Las descargas SAT leen estas credenciales de forma programática.{" "}
          <Link to="/descarga-automatica-sat" className="underline text-[#0F2D5C] font-medium">
            Ir a Descarga de CFDI SAT
          </Link>
        </p>
      ) : null}
    </div>
  );
}
